import React, { useCallback } from "react";
import { Layout, Page, Stack, Text, Columns } from "@shopify/polaris";
import { Form } from "react-final-form";

import StepsButtonActions from "../../../../../common/StepsButtonActions/StepsButtonActions.js";
import UpdatesFrequencyCard from "../UpdatesFrequencyCard/UpdatesFrequencyCard.js";
import BasedOnAttributesCard from "./components/BasedOnAttributesCard.js";
import UnusedProductsCard from "./components/UnusedProductsCard.js";
import { nextStep, updateAttributesOptions, updateUpdatesFrequency, updateStockOptions } from "../../../../../../store/features/action.js";
import { PRODUCTS_ACTION_PUSH_DOWN } from "../../../../../../../constants/productActionTypes.js";
import { openModal } from "../../../../../../store/features/modal.js";
import { OPEN_MODAL_COLLECTION_CHANGE_CONFIRM_MODAL } from "../../../../../../constants/modalTypes.js";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks/redux.js";
import NotificationPreviewModal from "../../../../../modals/NotificationPreviewModal/NotificationPreviewModal.js";
import useHandleKeyDown from "../../../../../../hooks/useHandleKeyDown.js";
import { validation } from "./validation.js";
import ValidationErrorsListBanner from "../../../../../common/ValidationErrorsListBanner/ValidationErrorsListBanner.js";

const AttributesTypeAndOptions = () => {
  const dispatch = useAppDispatch();
  const attributesOptions = useAppSelector((state) => state.action.actionData.options.attributesOptions);
  const stockOptions = useAppSelector((state) => state.action.actionData.options.stockOptions);
  const updatesFrequency = useAppSelector((state) => state.action.actionData.updatesFrequency);

  const onSubmit = useCallback((data) => {
    const dataWithoutUpdatesFrequency = { ...data };
    delete dataWithoutUpdatesFrequency.updatesFrequency;
    delete dataWithoutUpdatesFrequency.basedOnAttributesAction;
    delete dataWithoutUpdatesFrequency.actionNotify;
    dispatch(updateStockOptions({
      ...dataWithoutUpdatesFrequency,
      backStockNotify: data.actionNotify,
      lowStockNotify: data.actionNotify,
      outStockNotify: data.actionNotify,
    }));
    dispatch(updateAttributesOptions({ basedOnAttributesAction: data.basedOnAttributesAction, unusedProductsBasedOnAttributes: data.unusedProductsBasedOnAttributes }));
    dispatch(updateUpdatesFrequency(data.updatesFrequency));

    if (data.basedOnAttributesAction === PRODUCTS_ACTION_PUSH_DOWN) {
      dispatch(openModal({ modalType: OPEN_MODAL_COLLECTION_CHANGE_CONFIRM_MODAL }));
      return;
    }

    dispatch(nextStep());
  }, []);

  const handleKeyDown = useHandleKeyDown();

  return (
    <Page fullWidth>
      <NotificationPreviewModal actionType="ACTION_TYPE_ATTRIBUTES" />
      <Form
        onSubmit={onSubmit}
        validate={validation}
        initialValues={{ ...attributesOptions, updatesFrequency, actionNotify: stockOptions.backStockNotify }}
        render={({ handleSubmit, hasValidationErrors, submitFailed, errors }) => {
          const shouldShowBanner = hasValidationErrors && submitFailed;
          const errorMessages = Object.values(errors);

          return (
            <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
              <Stack vertical>
                <ValidationErrorsListBanner errors={errorMessages} show={shouldShowBanner} />
                <StepsButtonActions disabled={false} isNextLoading={false} />
                <Layout>
                  <Layout.Section>
                    <Columns columns={{ xs: "1fr 2fr" }}>
                      <Stack vertical spacing="tight">
                        <Text variant="headingMd" as="p">Products targeted based on their attributes</Text>
                        <Text variant="bodySm" as="p">
                          Stockhide will run this action when a product fits your attribute targeting rule that you’ve set up in step 2.
                        </Text>
                      </Stack>
                      <BasedOnAttributesCard />
                    </Columns>
                  </Layout.Section>
                  <Layout.Section>
                    <Columns columns={{ xs: "1fr 2fr" }}>
                      <Stack vertical spacing="tight">
                        <Text variant="headingMd" as="p">
                          Choose an action when products no longer meet the attribute
                        </Text>
                        <Text variant="bodySm" as="p">
                          Stockhide will run this action when a product no longer satisfy the attribute targeting rule you've set up in step 2.
                        </Text>
                      </Stack>
                      <UnusedProductsCard />
                    </Columns>
                  </Layout.Section>
                  <Layout.Section>
                    <Columns columns={{ xs: "1fr 2fr" }}>
                      <Stack vertical spacing="tight">
                        <Text variant="headingMd" as="p">Updates Frequency</Text>
                        <Text variant="bodySm" as="p">
                          Set how often you’d like to check for updates and take action accordingly.
                        </Text>
                      </Stack>
                      <UpdatesFrequencyCard />
                    </Columns>
                  </Layout.Section>
                </Layout>
              </Stack>
            </form>
          );
        }}
      />
    </Page>
  );
};

export default AttributesTypeAndOptions;